import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Bot, Phone, LogOut, ChevronLeft, ChevronRight,
  FlaskConical, History, Users, Search, FileText, Mail, Menu, X, Building2, Globe, CreditCard, Calendar, Brain,
  PhoneCall, PhoneForwarded, CheckCircle2, Megaphone, UserCog, ShieldCheck, Radio, Kanban, Home, BarChart3, Gift, Inbox,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useBrand } from '../contexts/BrandContext';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, badgeKey: null },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, badgeKey: null },
  { to: '/leads', label: 'Leads', icon: Users, badgeKey: 'hot_leads' },
  { to: '/leads/inbox', label: 'Lead Inbox', icon: Inbox, badgeKey: 'urgent_leads' },
  { to: '/pipeline', label: 'Pipeline', icon: Kanban, badgeKey: null },
  { to: '/listings', label: 'Listings', icon: Home, badgeKey: null },
  { to: '/appointments', label: 'Calendar', icon: Calendar, badgeKey: null },
  { to: '/ai-config', label: 'AI Brain', icon: Brain, badgeKey: null },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone, badgeKey: null },
  { to: '/scheduled-calls', label: 'Scheduled Calls', icon: PhoneCall, badgeKey: null },
  { to: '/properties', label: 'Properties', icon: Building2, badgeKey: null },
  { to: '/saved-searches', label: 'Saved Searches', icon: Search, badgeKey: null },
  { to: '/reports', label: 'Reports', icon: FileText, badgeKey: 'unread_alerts' },
  { to: '/call-history', label: 'Call History', icon: History, badgeKey: null },
  { to: '/spam', label: 'Spam Protection', icon: ShieldCheck, badgeKey: null },
  { to: '/call-test', label: 'Call Test', icon: FlaskConical, badgeKey: null },
  { to: '/call-simulation', label: 'Call Simulation', icon: Radio, badgeKey: null },
  { to: '/referrals', label: 'Growth & Referrals', icon: Gift, badgeKey: null },
  { to: '/settings/assistant', label: 'AI Assistant', icon: Bot, badgeKey: null },
  { to: '/settings/phone', label: 'Phone', icon: Phone, badgeKey: null },
  { to: '/settings/phone-menu', label: 'Phone Menu', icon: PhoneForwarded, badgeKey: null },
  { to: '/settings/reports', label: 'Report Settings', icon: Mail, badgeKey: null },
  { to: '/settings/lead-digest', label: 'Lead Digest', icon: Inbox, badgeKey: null },
  { to: '/settings/public-profile', label: 'Public Page', icon: Globe, badgeKey: null },
  { to: '/settings/billing', label: 'Billing', icon: CreditCard, badgeKey: null },
  { to: '/settings/account', label: 'Account', icon: UserCog, badgeKey: null },
];

export default function AppLayout({ children }) {
  const { user, token, logout } = useAuth();
  const { logoUrl, name: brandName } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ hot_leads: 0, unread_alerts: 0 });
  const [phoneInfo, setPhoneInfo] = useState(null);

  // Close mobile nav on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Poll badge counts every 30s
  const fetchBadges = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [alertRes, hotRes, phoneRes] = await Promise.all([
        axios.get(`${API}/reports/unread-count`, { headers }).catch(() => ({ data: { unread_alerts: 0 } })),
        axios.get(`${API}/leads/hot-count`, { headers }).catch(() => ({ data: { hot_leads: 0 } })),
        axios.get(`${API}/settings/phone`, { headers }).catch(() => ({ data: null })),
      ]);
      setBadges({
        unread_alerts: alertRes.data?.unread_alerts || 0,
        hot_leads: hotRes.data?.hot_leads || 0,
      });
      if (phoneRes.data?.phone_number) setPhoneInfo(phoneRes.data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const SidebarContent = ({ isMobile }) => (
    <>
      <div className="flex items-center gap-2.5 px-4 h-14 border-b border-border shrink-0">
        <div className="h-8 w-8 rounded-lg overflow-hidden shrink-0 ring-1 ring-[#38BDF8]/30 shadow-[0_0_14px_rgba(59,130,246,0.35)]"><img src={logoUrl} alt={brandName} className="h-full w-full object-cover" /></div>
        {(isMobile || !collapsed) && (
          <span className="text-sm font-semibold text-foreground tracking-tight">{brandName}</span>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ml-auto p-1 text-muted-foreground hover:text-foreground" data-testid="close-mobile-nav">
            <X size={20} />
          </button>
        )}
      </div>
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto a9-scroll">
        {navItems.map(({ to, label, icon: Icon, badgeKey }) => {
          const badgeCount = badgeKey ? (badges[badgeKey] || 0) : 0;
          return (
            <NavLink key={to} to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all relative ${
                  isActive
                    ? 'bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/25 shadow-[0_0_18px_-6px_rgba(59,130,246,0.55)]'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.03] border border-transparent'
                }`
              }
            >
              <span className="relative shrink-0">
                <Icon size={18} />
                {badgeCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-1 leading-none" data-testid={`badge-${badgeKey}`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </span>
              {(isMobile || !collapsed) && label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-border p-2 space-y-1">
        {!isMobile && (
          <button onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent w-full transition-colors"
            data-testid="toggle-sidebar"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!collapsed && 'Collapse'}
          </button>
        )}
        {(isMobile || !collapsed) && (
          <div className="px-3 py-1">
            <ThemeToggle compact />
          </div>
        )}
        {(isMobile || !collapsed) && user && (
          <div className="px-3 py-2">
            <p className="text-xs text-muted-foreground truncate">{user.email || user.full_name}</p>
          </div>
        )}
        <button onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 w-full transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={18} />
          {(isMobile || !collapsed) && 'Log out'}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} hidden md:flex flex-col border-r border-border bg-card transition-all duration-200`}>
        <SidebarContent isMobile={false} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full flex flex-col bg-card border-r border-border z-10 animate-in slide-in-from-left duration-200">
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar: mobile nav + phone number */}
        <header className="flex items-center justify-between px-4 h-14 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-3 md:hidden">
            <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent relative" data-testid="open-mobile-nav">
              <Menu size={22} />
              {(badges.hot_leads > 0 || badges.unread_alerts > 0) && (
                <span className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-destructive border-2 border-card" data-testid="mobile-badge-dot" />
              )}
            </button>
            <div className="h-7 w-7 rounded-md overflow-hidden"><img src={logoUrl} alt={brandName} className="h-full w-full object-cover" /></div>
            <span className="text-sm font-semibold text-foreground">{brandName}</span>
          </div>
          {/* Phone number display */}
          {phoneInfo?.phone_number ? (
            <div className="hidden md:flex items-center gap-2 ml-auto" data-testid="header-phone-number">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted border border-border">
                <PhoneCall size={14} className="text-primary" />
                <span className="text-sm font-mono text-foreground">{phoneInfo.phone_number}</span>
                <span className={`flex items-center gap-1 text-[10px] font-medium ${phoneInfo.is_active ? 'text-primary' : 'text-muted-foreground'}`}>
                  <CheckCircle2 size={10} /> {phoneInfo.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ) : (
            <div className="hidden md:block ml-auto" />
          )}
          {/* Notification Bell */}
          <div className="hidden md:flex items-center ml-2" data-testid="header-notification-bell">
            <NotificationBell />
          </div>
          {/* User avatar */}
          {user && (
            <div className="hidden md:flex items-center gap-2 ml-3 relative group" data-testid="header-user-avatar">
              <button
                onClick={() => navigate('/settings/account')}
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-accent transition-colors cursor-pointer"
                data-testid="profile-avatar-btn"
              >
                {user.profile_picture ? (
                  <img src={user.profile_picture} alt={user.full_name || 'User'} className="h-8 w-8 rounded-full object-cover border-2 border-border" referrerPolicy="no-referrer" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary/20 border-2 border-border flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary">{(user.full_name || user.email || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <span className="text-sm text-muted-foreground max-w-[120px] truncate">{user.full_name || 'Profile'}</span>
              </button>
            </div>
          )}
          {/* Mobile phone badge */}
          {phoneInfo?.phone_number && (
            <div className="flex md:hidden items-center gap-1.5 text-xs text-muted-foreground" data-testid="mobile-phone-number">
              <PhoneCall size={12} className="text-primary" />
              <span className="font-mono">{phoneInfo.phone_number}</span>
            </div>
          )}
          {/* Mobile notification bell */}
          <div className="flex md:hidden items-center ml-auto" data-testid="mobile-notification-bell">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-background">
          {children || <Outlet />}
          {/* Legal footer */}
          <div className="border-t border-border py-4 px-4 mt-auto">
            <div className="flex items-center justify-center gap-4">
              <a href="/terms" className="text-[11px] text-muted-foreground hover:text-primary transition-colors" data-testid="dashboard-footer-terms">Terms of Service</a>
              <span className="text-border">|</span>
              <a href="/privacy" className="text-[11px] text-muted-foreground hover:text-primary transition-colors" data-testid="dashboard-footer-privacy">Privacy Policy</a>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
